/**
 * Typed Analytics Client with Runtime Validation
 * Provides type-safe event tracking with schema validation
 */

import { AnalyticsEvent, EVENT_CATALOG, BaseEvent } from './events/schemas'

interface ValidationResult {
  isValid: boolean
  errors?: string[]
}

export class TypedAnalyticsClient {
  private posthog: any
  private sessionId: string
  private appVersion: string
  private userId?: string

  constructor(posthog: any) {
    this.posthog = posthog
    this.sessionId = this.generateSessionId()
    this.appVersion = process.env.REACT_APP_VERSION || '1.0.0'
  }

  // ============================================================================
  // CORE TRACKING METHODS
  // ============================================================================

  /**
   * Track a type-safe analytics event
   */
  track<T extends AnalyticsEvent>(event: Omit<T, keyof BaseEvent> & Partial<BaseEvent>): void {
    // Build complete event with base properties
    const completeEvent: T = {
      ...event,
      timestamp: event.timestamp || Date.now(),
      session_id: event.session_id || this.sessionId,
      app_version: event.app_version || this.appVersion,
      platform: event.platform || 'web',
      user_id: event.user_id || this.userId,
    } as T

    // Validate event schema in development
    if (process.env.NODE_ENV === 'development') {
      const validation = this.validateEvent(completeEvent)
      if (!validation.isValid) {
        console.error('❌ Invalid event schema:', validation.errors)
        console.error('Event:', completeEvent)
        throw new Error(`Invalid event schema for ${completeEvent.event_name}`)
      }
    }

    // Send to PostHog
    this.posthog?.capture(completeEvent.event_name, {
      ...completeEvent.properties,
      timestamp: completeEvent.timestamp,
      user_id: completeEvent.user_id,
      session_id: completeEvent.session_id,
      app_version: completeEvent.app_version,
      platform: completeEvent.platform,
    })

    // Log event for debugging in development
    if (process.env.NODE_ENV === 'development') {
      console.log('📊 Analytics Event:', completeEvent.event_name, completeEvent.properties)
    }

    // Store event for Event Tracker UI
    this.storeEventForTracker(completeEvent)
  }

  /**
   * Set the current user ID
   */
  setUserId(userId: string): void {
    this.userId = userId
    this.posthog?.identify(userId)
  }

  /**
   * Identify the current user with properties (email, name, etc.)
   * Called on login so PostHog person profile has email for error tracking templates.
   */
  identify(userId: string, properties?: Record<string, any>): void {
    this.userId = userId
    this.posthog?.identify(userId, properties)
  }

  /**
   * Clear the current user
   */
  clearUser(): void {
    this.userId = undefined
    this.posthog?.reset()
  }

  // ============================================================================
  // CONVENIENCE METHODS FOR COMMON EVENTS
  // ============================================================================

  /**
   * Track user login with full type safety
   */
  trackUserLogin(properties: {
    user_type: 'corporate' | 'coach' | 'member' | 'admin'
    role: string
    site_id: string
    login_method: 'email' | 'sso' | 'mobile' | 'google' | 'facebook'
    is_first_login: boolean
    login_duration_ms?: number
  }) {
    this.track({
      event_name: EVENT_CATALOG.USER_LOGIN,
      properties,
    })
  }

  /**
   * Track user logout with session analytics
   */
  trackUserLogout(properties: {
    user_type: 'corporate' | 'coach' | 'member' | 'admin'
    role: string
    site_id: string
    session_duration_minutes: number
    logout_method: 'manual' | 'timeout' | 'forced'
    pages_visited: number
    actions_performed: number
  }) {
    this.track({
      event_name: EVENT_CATALOG.USER_LOGOUT,
      properties,
    })
  }

  // ============================================================================
  // EVENT TRACKER UI METHODS
  // ============================================================================

  private storeEventForTracker(event: AnalyticsEvent): void {
    // Store events in localStorage for the Event Tracker UI
    try {
      const key = 'hapana_analytics_events'
      const stored = localStorage.getItem(key)
      const events = stored ? JSON.parse(stored) : []

      // Add new event
      events.unshift({
        ...event,
        id: Date.now() + '_' + Math.random().toString(36).substr(2, 9),
        tracked_at: new Date().toISOString(),
      })

      // Keep only last 100 events
      if (events.length > 100) {
        events.splice(100)
      }

      localStorage.setItem(key, JSON.stringify(events))
    } catch (error) {
      console.warn('Failed to store event for tracker:', error)
    }
  }

  /**
   * Get stored events for Event Tracker UI
   */
  getStoredEvents(): (AnalyticsEvent & { id: string; tracked_at: string })[] {
    try {
      const stored = localStorage.getItem('hapana_analytics_events')
      return stored ? JSON.parse(stored) : []
    } catch (error) {
      console.warn('Failed to get stored events:', error)
      return []
    }
  }

  /**
   * Clear stored events
   */
  clearStoredEvents(): void {
    localStorage.removeItem('hapana_analytics_events')
  }

  /**
   * Get event statistics for the tracker
   */
  getEventStats(): {
    totalEvents: number
    eventsByType: Record<string, number>
    eventsLast24h: number
    eventsToday: number
  } {
    const events = this.getStoredEvents()
    const now = Date.now()
    const oneDayAgo = now - 24 * 60 * 60 * 1000
    const todayStart = new Date().setHours(0, 0, 0, 0)

    const eventsByType: Record<string, number> = {}
    let eventsLast24h = 0
    let eventsToday = 0

    events.forEach((event) => {
      // Count by type
      eventsByType[event.event_name] = (eventsByType[event.event_name] || 0) + 1

      // Count last 24h
      if (event.timestamp > oneDayAgo) {
        eventsLast24h++
      }

      // Count today
      if (event.timestamp > todayStart) {
        eventsToday++
      }
    })

    return {
      totalEvents: events.length,
      eventsByType,
      eventsLast24h,
      eventsToday,
    }
  }

  // ============================================================================
  // VALIDATION METHODS
  // ============================================================================

  private validateEvent(event: AnalyticsEvent): ValidationResult {
    const errors: string[] = []

    // Base validation
    if (!event.event_name) {
      errors.push('event_name is required')
    }

    if (!event.timestamp || typeof event.timestamp !== 'number') {
      errors.push('timestamp must be a number')
    }

    if (!event.session_id) {
      errors.push('session_id is required')
    }

    return {
      isValid: errors.length === 0,
      errors: errors.length > 0 ? errors : undefined,
    }
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  private generateSessionId(): string {
    return (
      this.posthog?.get_session_id?.() ||
      'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
    )
  }
}

// Export singleton instance for easy use
let analyticsClient: TypedAnalyticsClient | null = null

export const initializeAnalytics = (posthog: any): TypedAnalyticsClient => {
  analyticsClient = new TypedAnalyticsClient(posthog)
  return analyticsClient
}

export const getAnalyticsClient = (): TypedAnalyticsClient => {
  if (!analyticsClient) {
    throw new Error('Analytics client not initialized. Call initializeAnalytics() first.')
  }
  return analyticsClient
}

// Export convenient tracking functions
export const trackUserLogin = (
  properties: Parameters<TypedAnalyticsClient['trackUserLogin']>[0]
) => {
  getAnalyticsClient().trackUserLogin(properties)
}

export const trackUserLogout = (
  properties: Parameters<TypedAnalyticsClient['trackUserLogout']>[0]
) => {
  getAnalyticsClient().trackUserLogout(properties)
}
